import React from "react";
import { LayoutGrid, List } from "lucide-react";
import { EDSButton } from "../../../../eds/EDSButton";
import { Preorder } from "../../../../../types/bookings";
import PreOrderCardView from "./PreOrderCardView";
import PreOrderListView from "./PreOrderListView";

type GroupedItem = {
  type: string;
  subType: string;
  time: string;
  name: string;
  totalQuantity: number;
  totalCost: number;
  price: number;
  customers: Array<{ name: string; quantity: number; seat?: string }>;
  description?: string;
  menuId?: string;
  isHeading?: boolean;
  preOrderIds: string[];
};

type GroupedPreOrders = Record<string, GroupedItem[]>;
type GroupedPreOrdersForCards = Record<string, Preorder[]>;

type PreOrderItemsSectionProps = {
  simpleView: boolean;
  setSimpleView: (value: boolean) => void;
  groupByType: boolean;
  setGroupByType: (value: boolean) => void;
  groupBySubType: boolean;
  setGroupBySubType: (value: boolean) => void;
  expandMenuItems: boolean;
  setExpandMenuItems: (value: boolean) => void;
  groupByTime: boolean;
  setGroupByTime: (value: boolean) => void;
  includeForNames: boolean;
  setIncludeForNames: (value: boolean) => void;
  manualPreOrders: Preorder[];
  groupedPreOrderItems: GroupedPreOrders;
  groupedPreOrdersForCards: GroupedPreOrdersForCards;
  hasActiveDisplayOptions: boolean;
  currency: string;
  guestCardClasses: (name?: string) => string;
  editingServiceCharge: string | null;
  serviceChargeValue: number;
  setServiceChargeValue: (value: number) => void;
  setEditingServiceCharge: (value: string | null) => void;
  handleSaveServiceCharge: (preOrderId: string) => Promise<void>;
  handleEditServiceCharge: (
    preOrderId: string,
    currentPercentage: number
  ) => void;
  editingComments: string | null;
  commentsValue: string;
  setCommentsValue: (value: string) => void;
  setEditingComments: (value: string | null) => void;
  handleSaveComments: (preOrderId: string) => Promise<void>;
  handleEditComments: (preOrderId: string, currentComments: string) => void;
  handleEditPreOrderItem: (preOrder: Preorder) => void;
  handleEditPreOrderItemById: (preOrderId: string) => void;
  handleDeletePreOrderItem: (preOrderId: string) => Promise<void>;
  handleAddPreOrderItem: () => void;
};

const PreOrderItemsSection = ({
  simpleView,
  setSimpleView,
  groupByType,
  setGroupByType,
  groupBySubType,
  setGroupBySubType,
  expandMenuItems,
  setExpandMenuItems,
  groupByTime,
  setGroupByTime,
  includeForNames,
  setIncludeForNames,
  manualPreOrders,
  groupedPreOrderItems,
  groupedPreOrdersForCards,
  hasActiveDisplayOptions,
  currency,
  guestCardClasses,
  editingServiceCharge,
  serviceChargeValue,
  setServiceChargeValue,
  setEditingServiceCharge,
  handleSaveServiceCharge,
  handleEditServiceCharge,
  editingComments,
  commentsValue,
  setCommentsValue,
  setEditingComments,
  handleSaveComments,
  handleEditComments,
  handleEditPreOrderItem,
  handleEditPreOrderItemById,
  handleDeletePreOrderItem,
  handleAddPreOrderItem
}: PreOrderItemsSectionProps): React.ReactElement => {
  return (
    <section className="py-4 px-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Items</h3>
        {/* View Toggle Icons */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSimpleView(false)}
            className={`p-1.5 rounded-md transition-colors ${
              !simpleView
                ? "bg-primary text-primary-foreground"
                : "bg-background text-foreground border border-border hover:bg-muted"
            }`}
            aria-pressed={!simpleView}
            aria-label="Card view"
            title="Card view"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSimpleView(true)}
            className={`p-1.5 rounded-md transition-colors ${
              simpleView
                ? "bg-primary text-primary-foreground"
                : "bg-background text-foreground border border-border hover:bg-muted"
            }`}
            aria-pressed={simpleView}
            aria-label="List view"
            title="List view"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Display Options - styled as clickable buttons in 2 rows */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <button
          type="button"
          onClick={() => setGroupByType(!groupByType)}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors text-center ${
            groupByType
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:bg-muted"
          }`}
          aria-pressed={groupByType}
          aria-label="Separate by type"
        >
          Separate by type
        </button>
        <button
          type="button"
          onClick={() => setGroupBySubType(!groupBySubType)}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors text-center ${
            groupBySubType
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:bg-muted"
          }`}
          aria-pressed={groupBySubType}
          aria-label="Separate by sub type"
        >
          Separate by sub type
        </button>
        <button
          type="button"
          onClick={() => setExpandMenuItems(!expandMenuItems)}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors text-center ${
            expandMenuItems
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:bg-muted"
          }`}
          aria-pressed={expandMenuItems}
          aria-label="Separate fixed price menu items"
        >
          Separate fixed price menu items
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <button
          type="button"
          onClick={() => setGroupByTime(!groupByTime)}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors text-center ${
            groupByTime
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:bg-muted"
          }`}
          aria-pressed={groupByTime}
          aria-label="Separate by time"
        >
          Separate by time
        </button>
        <button
          type="button"
          onClick={() => setIncludeForNames(!includeForNames)}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors text-center ${
            includeForNames
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:bg-muted"
          }`}
          aria-pressed={includeForNames}
          aria-label="Display for names"
        >
          Display &apos;for&apos; names
        </button>
      </div>

      {/* Simple View - Table format */}
      {simpleView && manualPreOrders.length > 0 && (
        <PreOrderListView
          groupedPreOrderItems={groupedPreOrderItems}
          hasActiveDisplayOptions={hasActiveDisplayOptions}
          groupByTime={groupByTime}
          includeForNames={includeForNames}
          currency={currency}
          handleEditPreOrderItemById={handleEditPreOrderItemById}
          handleDeletePreOrderItem={handleDeletePreOrderItem}
        />
      )}

      {/* Pretty View - Cards */}
      {!simpleView && manualPreOrders.length > 0 && (
        <PreOrderCardView
          groupedPreOrdersForCards={groupedPreOrdersForCards}
          hasActiveDisplayOptions={hasActiveDisplayOptions}
          currency={currency}
          guestCardClasses={guestCardClasses}
          editingServiceCharge={editingServiceCharge}
          serviceChargeValue={serviceChargeValue}
          setServiceChargeValue={setServiceChargeValue}
          setEditingServiceCharge={setEditingServiceCharge}
          handleSaveServiceCharge={handleSaveServiceCharge}
          handleEditServiceCharge={handleEditServiceCharge}
          editingComments={editingComments}
          commentsValue={commentsValue}
          setCommentsValue={setCommentsValue}
          setEditingComments={setEditingComments}
          handleSaveComments={handleSaveComments}
          handleEditComments={handleEditComments}
          handleEditPreOrderItem={handleEditPreOrderItem}
          handleDeletePreOrderItem={handleDeletePreOrderItem}
        />
      )}

      {/* No items message */}
      {manualPreOrders.length === 0 && (
        <div className="text-sm text-muted-foreground text-center py-4">
          No items added yet.
        </div>
      )}

      <div className="mt-4">
        <EDSButton variant="default" size="sm" onClick={handleAddPreOrderItem}>
          + Add Item
        </EDSButton>
      </div>
    </section>
  );
};

export default PreOrderItemsSection;
